import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';
import ApplicationForm from './components/ApplicationForm';

export default function FinancingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehiclePrice, setVehiclePrice] = useState(25000);
  const [downPayment, setDownPayment] = useState(5000);
  const [interestRate, setInterestRate] = useState(5.9);
  const [loanTerm, setLoanTerm] = useState(60);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [showApplication, setShowApplication] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  // Check if we should open the application form on load
  useEffect(() => {
    if (searchParams.get('apply') === 'true') {
      setShowApplicationForm(true);
      // Remove the query parameter immediately
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('apply');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]); // Add dependencies here

  // Calculate monthly payment
  useEffect(() => {
    const principal = vehiclePrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm;

    if (monthlyRate === 0) {
      setMonthlyPayment(principal / numPayments);
    } else {
      const payment =
        (principal * monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
        (Math.pow(1 + monthlyRate, numPayments) - 1);
      setMonthlyPayment(payment);
    }
  }, [vehiclePrice, downPayment, interestRate, loanTerm]);

  const scrollToApplication = () => {
    setShowApplication(true);
    setTimeout(() => {
      document.getElementById('application-section')?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-32 pb-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block bg-[#14B8A6]/10 text-[#14B8A6] px-5 py-2 rounded-full text-sm font-semibold mb-6">
            Financing Made Simple
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Calculate Your<br />Monthly Payment
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Get an instant estimate with our easy-to-use calculator and apply for pre-approval in minutes
          </p>
        </div>
      </div>

      {/* Calculator Section */}
      <div className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl p-10 shadow-2xl border border-gray-100">
            <div className="grid lg:grid-cols-5 gap-10">
              {/* Left: Calculator Controls */}
              <div className="lg:col-span-3 space-y-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Loan Calculator</h2>
                  <p className="text-gray-600">Adjust the values to see your estimated payment</p>
                </div>

                {/* Vehicle Price */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-900">
                      Vehicle Price
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">$</span>
                      <input
                        type="number"
                        value={vehiclePrice}
                        onChange={(e) => setVehiclePrice(Number(e.target.value))}
                        className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                        min="5000"
                        max="100000"
                        step="1000"
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    value={vehiclePrice}
                    onChange={(e) => setVehiclePrice(Number(e.target.value))}
                    min="5000"
                    max="100000"
                    step="1000"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#14B8A6]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$5,000</span>
                    <span>$100,000</span>
                  </div>
                </div>

                {/* Down Payment */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-900">
                      Down Payment
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={downPayment}
                        onChange={(e) => setDownPayment(Number(e.target.value))}
                        className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                        min="0"
                        max={vehiclePrice}
                        step="500"
                      />
                      <span className="text-sm text-gray-600">
                        ({((downPayment / vehiclePrice) * 100).toFixed(0)}%)
                      </span>
                    </div>
                  </div>
                  <input
                    type="range"
                    value={downPayment}
                    onChange={(e) => setDownPayment(Number(e.target.value))}
                    min="0"
                    max={vehiclePrice}
                    step="500"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#14B8A6]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$0</span>
                    <span>${vehiclePrice.toLocaleString()}</span>
                  </div>
                </div>

                {/* Interest Rate */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-semibold text-gray-900">
                      Interest Rate (APR)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={interestRate}
                        onChange={(e) => setInterestRate(Number(e.target.value))}
                        className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg text-right font-semibold"
                        min="0"
                        max="20"
                        step="0.1"
                      />
                      <span className="text-sm text-gray-600">%</span>
                    </div>
                  </div>
                  <input
                    type="range"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    min="0"
                    max="20"
                    step="0.1"
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#14B8A6]"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>20%</span>
                  </div>
                </div>

                {/* Loan Term */}
                <div>
                  <label className="text-sm font-semibold text-gray-900 block mb-3">
                    Loan Term
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {[36, 48, 60, 72].map((term) => (
                      <button
                        key={term}
                        onClick={() => setLoanTerm(term)}
                        className={`py-3 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                          loanTerm === term
                            ? 'bg-[#14B8A6] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {term} mo
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Payment Display */}
              <div className="lg:col-span-2 space-y-6">
                {/* Monthly Payment Display */}
                <div className="bg-gradient-to-br from-[#14B8A6] to-[#0d9488] rounded-2xl p-8 text-white">
                  <p className="text-sm opacity-90 mb-2">Estimated Monthly Payment</p>
                  <p className="text-5xl font-bold mb-1">
                    ${monthlyPayment.toFixed(0)}
                  </p>
                  <p className="text-sm opacity-80">
                    per month for {loanTerm} months
                  </p>
                </div>

                {/* Payment Breakdown */}
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h4 className="font-bold text-gray-900 mb-4 text-sm">Payment Breakdown</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Vehicle Price</span>
                      <span className="font-semibold text-gray-900 text-sm">
                        ${vehiclePrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Down Payment</span>
                      <span className="font-semibold text-gray-900 text-sm">
                        -${downPayment.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-300">
                      <span className="text-sm text-gray-600">Amount Financed</span>
                      <span className="font-semibold text-gray-900 text-sm">
                        ${(vehiclePrice - downPayment).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Interest</span>
                      <span className="font-semibold text-gray-900 text-sm">
                        ${(monthlyPayment * loanTerm - (vehiclePrice - downPayment)).toFixed(0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t-2 border-gray-300">
                      <span className="font-bold text-gray-900 text-sm">Total Cost</span>
                      <span className="font-bold text-gray-900 text-base">
                        ${(monthlyPayment * loanTerm + downPayment).toFixed(0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <button 
                  onClick={() => setShowApplicationForm(true)}
                  className="w-full py-4 bg-[#14B8A6] text-white rounded-full font-semibold hover:bg-[#0d9488] transition-colors cursor-pointer whitespace-nowrap shadow-lg"
                >
                  Get Pre-Approved Now
                </button>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-[#14B8A6]"></i>
              </div>
              <p className="text-sm text-gray-700">
                This calculator provides estimates only. Actual rates and terms may vary based on credit approval, vehicle condition, and lender requirements.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Our Financing</h2>
            <p className="text-lg text-gray-600">Fast approval, competitive rates, and transparent terms</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-[#14B8A6]/10 rounded-full mb-6">
                <i className="ri-percent-line text-2xl text-[#14B8A6]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Rates as low as 3.9% APR</h3>
              <p className="text-gray-600 leading-relaxed">Competitive financing options for qualified buyers with excellent credit history.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-[#14B8A6]/10 rounded-full mb-6">
                <i className="ri-time-line text-2xl text-[#14B8A6]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Instant pre-approval decisions</h3>
              <p className="text-gray-600 leading-relaxed">Get your financing decision in minutes, not days. Simple online application process.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="w-14 h-14 flex items-center justify-center bg-[#14B8A6]/10 rounded-full mb-6">
                <i className="ri-shield-check-line text-2xl text-[#14B8A6]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">No hidden fees or surprises</h3>
              <p className="text-gray-600 leading-relaxed">Transparent pricing with all costs clearly outlined before you commit to purchase.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Application Form Section */}
      {showApplication && (
        <div id="application-section" className="py-16 px-6 bg-white scroll-mt-24">
          <div className="max-w-4xl mx-auto">
            <ApplicationForm />
          </div>
        </div>
      )}

      {/* Application Form Modal */}
      {showApplicationForm && (
        <ApplicationForm onClose={() => setShowApplicationForm(false)} />
      )}

      <Footer />
    </div>
  );
}
